import React, { FC, useEffect } from 'react';

import { FormikValues, useFormik } from 'formik';

import {
  BookingState,
  getHourlyRate,
  ICalendarEventType,
  pharmacistBookingDetails,
  PharmacyLegendVariant,
  pharmacyWorkshift,
  updateTimings,
} from '@pharmaplan/common';

import { pharmacyInitialState } from '@pharmaplan/common/reducers/pharmacyReducer';
import PharmacistSummary from '../PharmacistSummary/PharmacistSummary';
import { appendTimeToDate } from '../../../helpers/workshift/functions';
import useSelfService from '../../../hooks/useSelfService';
import useWrokshiftSummaryModals from '../../../hooks/workshiftSummary/useWorkshiftSummaryModal';
import useWorkshiftSummary from '../../../hooks/workshiftSummary/useWorkshiftSummary';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { selectFooterPaginationCurrent } from '../../../selectors/drawerSelector';
import {
  setBack,
  setButtons,
  setFooterShow,
  setPagination,
  setTitle,
} from '../../../actions/drawerActions';
import { isPastServerDate } from '../../../helpers/Functions';

import { MomentTimeFormats } from '../../../helpers/Constants';

import UpdateDateTimeRange from './UpdateDateTimeRange/UpdateDateTimeRange';
import Validator from './Validator';
import InfoBox from './InfoBox/InfoBox';

interface IWorkshiftSummary {
  eventIds: Array<string>;
  type: ICalendarEventType;
  bookingState: BookingState;
  preventInteraction?: boolean;
}

const { fullDate } = MomentTimeFormats;

const WorkshiftSummary: FC<IWorkshiftSummary> = ({
  type,
  eventIds,
  bookingState,
  preventInteraction,
}) => {
  const dispatch = useAppDispatch();

  const { isSelfService } = useSelfService();

  const current = useAppSelector(selectFooterPaginationCurrent);

  const currentWorkshift = useAppSelector(pharmacyWorkshift);
  const pharmacistDets = useAppSelector(pharmacistBookingDetails);

  const getDateFrom = type === PharmacyLegendVariant.booked ? pharmacistDets : currentWorkshift;

  const { startDate, endDate } = getDateFrom ?? {};

  const id = eventIds[current - 1];
  const totalEvents = eventIds.length;

  const handleSubmit = (values: FormikValues) => {
    const { startTime, endTime } = values ?? {};

    dispatch(
      updateTimings({
        bookingId: id,
        startDate: appendTimeToDate(startDate, startTime).toFormat(fullDate),
        endDate: appendTimeToDate(endDate, endTime).toFormat(fullDate),
      }),
    );
  };

  const formik = useFormik({
    initialValues: {
      startTime: startDate,
      endTime: endDate,
    },

    onSubmit: handleSubmit,
    validationSchema: Validator(),
  });

  const { fetchEvents, handleCancellation, dispatchActions } = useWorkshiftSummary(
    eventIds,
    type,
    bookingState,
    formik,
    preventInteraction,
  );
  const { show: showDateTime, handleActions } = useWrokshiftSummaryModals(
    eventIds,
    type,
    formik,
  );

  const dateTime = showDateTime ? (
    <UpdateDateTimeRange formik={formik} />
  ) : null;
  const { component, event, title = '' } = fetchEvents();

  const setDrawerTitle = () => {
    const drawerTitle = title;

    dispatch(setTitle({ title: drawerTitle }));
  };

  const disableDrawerBackButton = () => {
    dispatch(setBack(null));
  };

  const setDrawerButtons = () => {
    dispatch(
      setButtons({
        show: !isPastServerDate(startDate),
        ...(isSelfService ? handleActions() : handleCancellation()),
      }),
    );
  };

  const setDrawerPagination = () => {
    dispatch(
      setPagination({
        show: true,
        total: totalEvents,
        current,
      }),
    );
  };

  useEffect(() => {
    setDrawerTitle();
    disableDrawerBackButton();
    setDrawerPagination();
    if (!preventInteraction) {
      dispatch(setFooterShow(true));
      setDrawerButtons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDateTime, getDateFrom]);

  useEffect(() => {
    id && dispatchActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    dispatch(getHourlyRate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <InfoBox bookingState={bookingState} eventType={type} />

      <PharmacistSummary
        DateTime={dateTime}
        event={event ?? pharmacyInitialState.workshift}
        type={type}
      />

      {component}
    </>
  );
};

export default WorkshiftSummary;
